import { useState } from "react";
import { Card, Text, SimpleGrid, Button, Group, Select } from "@mantine/core";
import { IconPlus } from "@tabler/icons-react";
import { supportApi } from "../../api/support";
import { LostItemFormModal, type LostItemCreateBody } from "./LostItemFormModal";
import { type LostItem, refName, refPhone } from "../../types/domain";
import { usePaginatedList } from "../../hooks/usePaginatedList";
import { PageHeader } from "../../components/common/PageHeader";
import { DataTable, type Column } from "../../components/common/DataTable";
import {
  ListDateRangeFilter,
  type DateRange,
} from "../../components/common/ListDateRangeFilter";
import { ExportMenu } from "../../components/common/ExportMenu";
import { collectAll, type ExportField } from "../../lib/export";
import { StatusBadge } from "../../components/common/StatusBadge";
import { TripLink } from "../../components/common/TripLink";
import { ZoomableImage } from "../../components/common/ZoomableImage";
import {
  DetailDrawer,
  DetailField,
  DetailSection,
} from "../../components/common/DetailDrawer";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { notifications } from "@mantine/notifications";
import { useAuth } from "../../auth/useAuth";
import { StatusUpdater } from "../../components/common/StatusUpdater";
import { humanize, toSelectOptions } from "../../lib/labels";
import { formatDateTime } from "../../lib/format";
import { errorMessage } from "../../lib/axios";

const LOST_ITEM_STATUSES = [
  "pending",
  "inProgress",
  "completedFound",
  "completedNotFound",
];
const LOST_ITEM_STATUS_OPTIONS = toSelectOptions(
  LOST_ITEM_STATUSES,
  "All statuses",
);

const lostItemExportFields: ExportField<LostItem>[] = [
  { label: "Ticket", value: (it) => it.ticketNo ?? it._id },
  { label: "Item", value: (it) => it.itemType },
  { label: "Description", value: (it) => it.message },
  {
    label: "Reporter",
    value: (it) => refName(it.userID) ?? refName(it.driverID),
  },
  { label: "Status", value: (it) => it.status },
  { label: "Reported", value: (it) => it.createdAt },
];

export function LostItemsPage() {
  const qc = useQueryClient();
  const { admin } = useAuth();
  const [selected, setSelected] = useState<LostItem | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [status, setStatus] = useState("");
  const [range, setRange] = useState<DateRange>({});
  const filters = { status, startDate: range.startDate, endDate: range.endDate };
  const { page, setPage, result, isLoading, error } = usePaginatedList<LostItem>({
    queryKey: ["lost-items"],
    fetcher: supportApi.listLostItems,
    filters,
  });

  const createMutation = useMutation({
    mutationFn: (body: LostItemCreateBody) => supportApi.createLostItem(body),
    onSuccess: () => {
      notifications.show({ color: "green", message: "Lost item created" });
      setFormOpen(false);
      void qc.invalidateQueries({ queryKey: ["lost-items"] });
    },
    onError: (e) =>
      notifications.show({ color: "red", message: errorMessage(e) }),
  });

  const updateStatus = useMutation({
    mutationFn: ({ id, status, comment }: { id: string; status: string; comment: string }) =>
      supportApi.updateLostItemStatus(id, {
        status,
        comment,
        updatedBy: admin?.id ?? "",
      }),
    onSuccess: (updated) => {
      notifications.show({ color: "green", message: "Status updated" });
      setSelected(updated);
      void qc.invalidateQueries({ queryKey: ["lost-items"] });
    },
    onError: (e) =>
      notifications.show({ color: "red", message: errorMessage(e) }),
  });

  const columns: Column<LostItem>[] = [
    {
      key: "ticket",
      header: "Ticket",
      render: (it) => (
        <Text size="sm" ff="monospace">
          {it.ticketNo ?? it._id.slice(-8)}
        </Text>
      ),
    },
    { key: "type", header: "Item", render: (it) => humanize(it.itemType) },
    {
      key: "description",
      header: "Description",
      render: (it) => (
        <Text size="sm" c="dimmed" lineClamp={1} maw={320}>
          {it.message ?? "—"}
        </Text>
      ),
    },
    {
      key: "reporter",
      header: "Reporter",
      render: (it) => refName(it.userID) ?? refName(it.driverID) ?? "—",
    },
    { key: "trip", header: "Trip", render: (it) => <TripLink trip={it.tripID} /> },
    { key: "status", header: "Status", render: (it) => <StatusBadge status={it.status} /> },
    {
      key: "reported",
      header: "Reported",
      render: (it) => (
        <Text size="xs" c="dimmed">
          {formatDateTime(it.createdAt)}
        </Text>
      ),
    },
  ];

  return (
    <>
      <PageHeader
        title="Lost & Found"
        subtitle="Items reported on trips"
        actions={
          <Button
            leftSection={<IconPlus size={16} />}
            onClick={() => setFormOpen(true)}
          >
            New lost item
          </Button>
        }
      />
      <Card withBorder radius="md" padding="lg">
        <Group mb="md" gap="sm">
          <Select
            data={LOST_ITEM_STATUS_OPTIONS}
            value={status}
            onChange={(v) => {
              setStatus(v ?? "");
              setPage(1);
            }}
            w={180}
            allowDeselect={false}
          />
          <ListDateRangeFilter
            value={range}
            onChange={(r) => {
              setRange(r);
              setPage(1);
            }}
          />
          <ExportMenu
            filename="lost-items"
            fields={lostItemExportFields}
            fetchAll={() =>
              collectAll(supportApi.listLostItems, {
                ...filters,
              })
            }
          />
        </Group>
        <DataTable
          columns={columns}
          rows={result?.items ?? []}
          rowKey={(it) => it._id}
          loading={isLoading}
          error={error ? errorMessage(error) : null}
          emptyLabel="No lost items reported"
          onRowClick={setSelected}
          page={page}
          totalPages={result?.totalPages}
          total={result?.total}
          onPageChange={setPage}
        />
      </Card>

      <DetailDrawer
        opened={!!selected}
        onClose={() => setSelected(null)}
        title={selected?.ticketNo ?? "Lost item"}
      >
        {selected && (
          <>
            <DetailSection title="Item">
              <DetailField label="Type" value={humanize(selected.itemType)} />
              <DetailField label="Status" value={<StatusBadge status={selected.status} />} />
              <DetailField label="Reported" value={formatDateTime(selected.createdAt)} />
              <DetailField label="Description" value={selected.message} />
            </DetailSection>
            <DetailSection title="Trip & people">
              <DetailField label="Trip" value={<TripLink trip={selected.tripID} />} />
              <DetailField label="Passenger" value={refName(selected.userID)} />
              <DetailField label="Passenger phone" value={refPhone(selected.userID)} />
              <DetailField label="Driver" value={refName(selected.driverID)} />
              <DetailField label="Driver phone" value={refPhone(selected.driverID)} />
            </DetailSection>
            {!!selected.images?.length && (
              <DetailSection title="Photos">
                <SimpleGrid cols={2}>
                  {selected.images.map((src, i) => (
                    <ZoomableImage key={i} src={src} h={120} title={`${selected.ticketNo} — photo ${i + 1}`} />
                  ))}
                </SimpleGrid>
              </DetailSection>
            )}
            <DetailSection title="Manage">
              <StatusUpdater
                current={selected.status}
                statuses={LOST_ITEM_STATUSES}
                loading={updateStatus.isPending}
                onSubmit={(status, comment) =>
                  updateStatus.mutate({ id: selected._id, status, comment })
                }
              />
            </DetailSection>
          </>
        )}
      </DetailDrawer>

      <LostItemFormModal
        opened={formOpen}
        onClose={() => setFormOpen(false)}
        loading={createMutation.isPending}
        onSubmit={(body) => createMutation.mutate(body)}
      />
    </>
  );
}
